<template>
  <app-collapse
    :heading="`Linked identifiers (${totalIdentifiers})`"
    :is-open="true"
  >
    <div class="space-y-4 mx-0.5">
      <div class="space-y-3">
        <identifiers-search-field
          size="sm"
          :has-shortcut="false"
          :store="recipientIdentifiersStore"
        />
        <p
          v-if="searchText && identifierResultsCount"
          v-html="paginationSummary"
          class="text-xs mx-2"
        />
      </div>
      <template v-if="isFetching && !isFetchingNextPage">
        <div class="skeleton h-62" />
      </template>
      <template v-else-if="!identifiers.length">
        <app-empty-state
          size="sm"
          :icon="emptyState.icon"
          :title="emptyState.title"
          :description="emptyState.description"
          container-class="py-10 px-6 bg-gray-100 rounded-xl"
        >
          <template #actions>
            <transfer-recipient-identifier-modal
              v-if="!searchText"
              :agency-id="agencyId"
              :transfer-recipient-id="transferRecipientId"
              modal-opener-btn-class="btn-xs"
            />
          </template>
        </app-empty-state>
      </template>
      <template v-else>
        <app-card
          variant="muted"
          body-class="space-y-3"
        >
          <agency-identifier-display-label
            v-for="(identifier, idx) in identifiers"
            :key="idx"
            :count="idx + 1"
            :identifier="identifier"
          />
        </app-card>
        <button
          v-if="hasNextPage"
          class="btn btn-secondary w-full"
          :disabled="isFetchingNextPage"
          @click="fetchNextPage"
        >
          {{ isFetchingNextPage ? 'Loading...' : 'Load more' }}
        </button>
      </template>
    </div>
  </app-collapse>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useFiltersReset } from '@/composables/useFiltersReset';
import { useInfiniteIdentifiersByTransferRecipientId } from '@/services/transfer-recipients/queries.js';
import { useTransferRecipientIdentifiersStore } from '@/stores/transferRecipients.js';
import TransferRecipientIdentifierModal from '@/components/transfer-recipients/TransferRecipientIdentifierModal.vue';
import AgencyIdentifierDisplayLabel from '@/components/agencies/AgencyIdentifierDisplayLabel.vue';
import IdentifiersSearchField from '../identifiers/IdentifiersSearchField.vue';

const props = defineProps({
  transferRecipientId: {
    type: String,
    required: true,
  },
  agencyId: {
    type: String,
    required: true,
  },
});
const recipientId = computed(() => props.transferRecipientId);
const recipientIdentifiersStore = useTransferRecipientIdentifiersStore();
const searchText = computed(() => recipientIdentifiersStore.filters.search_text);

useFiltersReset(recipientIdentifiersStore);

watch(
  () => props.transferRecipientId,
  () => {
    recipientIdentifiersStore.resetFilters();
  }
);

const {
  data: identifiersByTransferRecipientData,
  hasNextPage,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
} = useInfiniteIdentifiersByTransferRecipientId(recipientId);

const identifiersByTransferRecipient = computed(() => {
  const data = identifiersByTransferRecipientData.value?.pages?.flatMap((page) => page.data) ?? [];
  const meta = identifiersByTransferRecipientData.value?.pages?.[0]?.meta;
  return { data, meta };
});

const identifiers = computed(() => identifiersByTransferRecipient.value?.data ?? []);
const identifierResultsCount = computed(() => identifiers.value.length);
const totalIdentifiers = computed(() => identifiersByTransferRecipient.value?.meta?.pagination?.total || 0);

const countLabel = computed(() => {
  if (identifierResultsCount.value === totalIdentifiers.value) {
    return identifierResultsCount.value;
  }
  return `${identifierResultsCount.value} of ${totalIdentifiers.value}`;
});

const paginationSummary = computed(() => {
  return `Showing ${countLabel.value} results for <span class="text-brand-600">'${searchText.value}'</span>`;
});

const emptyState = computed(() => {
  return {
    true: {
      icon: 'face-sad',
      title: 'No identifiers matched your search',
      description: `We couldn't find anything matching “${searchText.value}” in identifiers. Please try again.`,
    },
    false: {
      icon: 'link-broken-two',
      title: 'No identifiers are linked',
      description: 'To start receiving payments with this beneficiary, you must link identifiers.',
    },
  }[!!searchText.value];
});
</script>
